import type { ReactNode } from 'react';
import { darkTheme } from '../../../lib/tokens';
import type { PipelineArtifact, PipelineGap, PipelineLesson, PipelineRefresh, PipelineSource } from '../data/types';

export function Section({
  title,
  lead,
  children,
  large = false,
}: {
  title: string;
  lead?: string;
  children: ReactNode;
  large?: boolean;
}): JSX.Element {
  return (
    <section className="mt-8">
      <h2 className={`font-bold text-text-primary ${large ? 'text-2xl' : 'text-lg'}`}>{title}</h2>
      {lead && <p className={`mt-1 text-text-secondary ${large ? 'text-lg' : 'text-sm'}`}>{lead}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function ArtifactList({ artifacts, large = false }: { artifacts: PipelineArtifact[]; large?: boolean }): JSX.Element {
  return (
    <ul className="space-y-2">
      {artifacts.map((a) => (
        <li
          key={a.name}
          className="rounded-md border p-3"
          style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
        >
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className={`font-semibold text-text-primary ${large ? 'text-base' : 'text-sm'}`}>{a.name}</span>
            <code className={`font-mono text-text-tertiary ${large ? 'text-sm' : 'text-[11px]'} break-all`}>{a.path}</code>
          </div>
          <p className={`mt-1 text-text-secondary ${large ? 'text-base' : 'text-xs'}`}>{a.purpose}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Vorfall und die Regel, die daraus wurde. Der Vorfall steht zuerst — eine
 * Regel ohne ihren Anlass klingt wie eine Meinung.
 */
export function LessonList({ lessons, large = false }: { lessons: PipelineLesson[]; large?: boolean }): JSX.Element {
  return (
    <ul className="space-y-3">
      {lessons.map((l) => (
        <li
          key={l.rule.slice(0, 40)}
          className="rounded-md border-l-4 p-4"
          style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.warning }}
        >
          <p className={`text-text-secondary ${large ? 'text-lg' : 'text-sm'}`}>{l.incident}</p>
          <p className={`mt-2 font-semibold text-text-primary ${large ? 'text-lg' : 'text-sm'}`}>
            <span className="uppercase tracking-wider" style={{ color: darkTheme.colors.success }}>
              Daraus:{' '}
            </span>
            {l.rule}
          </p>
          {l.ticket && <p className="mt-1 font-mono text-[11px] text-text-tertiary">{l.ticket}</p>}
        </li>
      ))}
    </ul>
  );
}

export function GapList({ gaps, large = false }: { gaps: PipelineGap[]; large?: boolean }): JSX.Element {
  return (
    <ul className="space-y-3">
      {gaps.map((g) => (
        <li
          key={g.topic}
          className="rounded-md border p-4"
          style={{ background: darkTheme.colors.bgSurface, borderColor: `${darkTheme.colors.warning}4d` }}
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className={`font-semibold text-text-primary ${large ? 'text-lg' : 'text-sm'}`}>{g.topic}</h3>
            <span
              className="rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"
              style={{ background: darkTheme.colors.warningBg, color: darkTheme.colors.warning }}
            >
              {g.state}
            </span>
          </div>
          <p className={`mt-2 text-text-secondary ${large ? 'text-lg' : 'text-sm'}`}>{g.why}</p>
        </li>
      ))}
    </ul>
  );
}

export function SourceList({ sources }: { sources: PipelineSource[] }): JSX.Element {
  return (
    <ul className="space-y-1.5">
      {sources.map((s) => (
        <li key={s.path} className="flex flex-wrap items-baseline gap-x-3 text-sm">
          <span className="text-text-secondary">{s.label}</span>
          <code className="font-mono text-[11px] text-text-tertiary break-all">{s.path}</code>
        </li>
      ))}
    </ul>
  );
}

/**
 * Wie man die Zahlen neu erhebt. Gehoert auf die Seite, weil ein Stand-Datum
 * ohne den Weg zum naechsten Stand nur eine Ausrede ist.
 */
export function RefreshList({ items }: { items: PipelineRefresh[] }): JSX.Element {
  return (
    <ul className="space-y-1.5">
      {items.map((r) => (
        <li key={r.command} className="text-sm">
          <span className="text-text-secondary">{r.what}</span>
          <code className="mt-0.5 block font-mono text-[11px] text-text-tertiary break-all">{r.command}</code>
        </li>
      ))}
    </ul>
  );
}
